import {
  getGuestPassengersKey,
  getGuestVehiclesKey,
  useGetGuestPassenger,
  useGetGuestVehicle,
  useUpdateGuestPassenger,
  useUpdateGuestVehicle,
} from 'api-hooks/guest';
import NavigationEnum from 'common/navigation';
import { queryClient } from 'common/repositories/query-client';
import Separator from 'components/commons/separator';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import { styles } from 'modules/home/style';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { User } from 'react-feather';
import { useToasts } from 'react-toast-notifications';
import color from 'styles/color';

import PassengerGuestForm from './components/passenger-form';
import VehicleGuestForm from './components/vehicle-form';

interface Props {
  type: 'default' | 'vehicle';
}

export default function GuestView(props: Props) {
  const { type } = props;

  const router = useRouter();
  const { t } = useTranslation();
  const toast = useToasts();
  const id = router.query['id'] as string;

  const { data: dataPassenger, isLoading: loadingPassenger } =
    useGetGuestPassenger({ id }, { enabled: type === 'default', retry: 3 });

  const { data: dataVehicle, isLoading: loadingVehicle } = useGetGuestVehicle(
    { id },
    { enabled: type === 'vehicle', retry: 3 },
  );

  const { mutateAsync: updatePassenger } = useUpdateGuestPassenger();
  const { mutateAsync: updateVehicle } = useUpdateGuestVehicle();

  const passenger = dataPassenger?.data;
  const vehicle = dataVehicle?.data;

  const loading = React.useMemo(() => {
    if (type === 'default') return loadingPassenger;
    return loadingVehicle;
  }, [loadingPassenger, loadingVehicle, type]);

  return (
    <>
      <FetchWrapperComponent
        isLoading={loading}
        loadingComponent={
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className="loader" />
          </div>
        }
        component={
          <Stack
            rootCss={{
              ...styles.breakPointsCss,
              margin: 'auto',
              minHeight: '100vh',
            }}
          >
            <Separator direction="vertical" gap={42} />
            <div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <User size={32} color={color.primary} />
                  <Separator gap={16} />
                  <Text variant="headline4">{t('common:guest_list')}</Text>
                </div>
                <Separator gap={24} />
                {type === 'default' ? (
                  <PassengerGuestForm
                    passenger={passenger}
                    onSubmit={async (input, form) => {
                      const result = await updatePassenger({ id, body: input });

                      form.reset();
                      queryClient.refetchQueries([getGuestPassengersKey()[0]]);

                      router.push(NavigationEnum.guestList);

                      toast.addToast(result.message, {
                        appearance: 'success',
                        autoDismiss: true,
                      });

                      return result.data;
                    }}
                  />
                ) : (
                  <VehicleGuestForm
                    vehicle={vehicle}
                    onSubmit={async (input, form) => {
                      const result = await updateVehicle({ id, body: input });

                      form.reset();
                      queryClient.refetchQueries([getGuestVehiclesKey()[0]]);

                      router.push(NavigationEnum.guestList);

                      toast.addToast(result.message, {
                        appearance: 'success',
                        autoDismiss: true,
                      });

                      return result.data;
                    }}
                  />
                )}
              </div>
            </div>

            <Separator direction="vertical" gap={42} />
          </Stack>
        }
      />
    </>
  );
}
